<!-- eslint-disable camelcase -->
<script setup lang="ts">
import {
  computed, watch, onMounted, ref, nextTick,
} from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import { array, object, string } from 'yup';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import type { ContactPayload, Address } from '@apparatix/types/Contacts';
import type { Address as AccountAddress } from '@apparatix/types/Account';

const props = withDefaults(
  defineProps<{
    isLoading: boolean
    address?: Address|AccountAddress
  }>(),
  {
    address: () => null,
  },
);
const emit = defineEmits<{ (e: 'on:submit', contact: ContactPayload): void }>();

const {
  defineField, errors, handleSubmit, meta, resetForm,
} = useForm({
  validationSchema: toTypedSchema(object({
    first_name: string().required().default(''),
    last_name: string().optional().default(''),
    job_title: string().optional().default(''),
    addresses: array(object({
      address_line1: string().optional().default(''),
      address_line2: string().optional().default(''),
      city: string().optional().default(''),
      region: string().optional().default(''),
      postal_code: string().optional().default(''),
    })),
    address_phone_numbers: array(object({ number: string().optional().default('') })),
    address_emails: array(object({
      email: string().email({ message: 'Please enter a valid email address' }).matches(
        /^(?:[^\s@]+@[^\s@]+\.[^\s@]{2,})?$/,
        'Enter a valid email with a proper domain',
      ).optional().default(''),
    })),
  })),
});

const [firstName, firstNameAttrs] = defineField('first_name');
const [lastName, lastNameAttrs] = defineField('last_name');
const [title, titleAttrs] = defineField('job_title');
const [email, emailAttrs] = defineField('address_emails.0.email');
const [phone, phoneAttrs] = defineField('address_phone_numbers.0.number');
const [address1, address1Attrs] = defineField('addresses.0.address_line1');
const [address2, address2Attrs] = defineField('addresses.0.address_line2');
const [city, cityAttrs] = defineField('addresses.0.city');
const [state, stateAttrs] = defineField('addresses.0.region');
const [zip, zipAttrs] = defineField('addresses.0.postal_code');

const disableSubmit = computed(() => {
  if (props.isLoading) return true;
  return !meta.value.valid;
});

const submitLabel = computed(() => props.isLoading ? 'Saving...' : 'Add Contact');

const onSubmit = handleSubmit(values => {
  const payload = values as ContactPayload;
  if (payload.address_emails) {
    payload.address_emails[0].addressType = 'work';
  }
  if (payload.address_phone_numbers) {
    payload.address_phone_numbers[0].addressType = 'work';
  }

  emit('on:submit', payload);
});

function getInitialValues(addr) {
  const addressData = addr
    ? {
      address_line1: addr.address_line1 ?? addr.addressLine1 ?? '',
      address_line2: addr.address_line2 ?? addr.addressLine2 ?? '',
      address_line3: addr.address_line3 ?? addr.addressLine3 ?? '',
      city: addr.city ?? '',
      region: addr.region ?? '',
      postal_code: addr.postal_code ?? addr.postalCode ?? '',
    }
    : {};

  return {
    values: {
      first_name: '',
      last_name: '',
      job_title: '',
      addresses: [addressData],
      address_phone_numbers: [{ number: '' }],
      address_emails: [{ email: '' }],
    },
  };
}

onMounted(() => {
  resetForm(getInitialValues(props.address));
  focusFirstName();
});

watch(() => props.address, (addr) => {
  if (addr) {
    resetForm(getInitialValues(addr));
  }
});

defineExpose({ resetForm });

const firstNameInputRef = ref(null);


const focusFirstName = async () => {
  await nextTick();
  const el = firstNameInputRef.value?.$el as HTMLElement | undefined;
  const input = el?.tagName === 'INPUT' ? (el as HTMLInputElement) : (el?.querySelector('input') as HTMLInputElement | null);
  input?.focus();
};

</script>
<template>
  <div class="flex flex-col gap-3 w-full">
    <h2 class="font-medium text-sm text-[var(--swimlane-secondary-text)]">
      Create a New Contact
    </h2>
    <form @submit="onSubmit">
      <div class="w-full grid grid-cols-[auto,1fr] grid-rows-auto gap-4">
        <div class="flex items-center">
          <p class="apx-small-label">
            Name
          </p>
        </div>
        <div class="flex gap-4">
          <div class="flex-1 items-center">
            <InputText
              ref="firstNameInputRef"
              v-model="firstName"
              v-bind="firstNameAttrs"
              placeholder="First name"
              class="w-full"
              :invalid="Boolean(errors.first_name)"
              :disabled="props.isLoading"
            />
          </div>
          <div class="flex-1 items-center">
            <InputText
              v-model="lastName"
              v-bind="lastNameAttrs"
              placeholder="Last name"
              class="w-full"
              :invalid="Boolean(errors.last_name)"
              :disabled="props.isLoading"
            />
          </div>
        </div>
        <div class="flex items-center">
          <p class="apx-small-label">
            Title
          </p>
        </div>
        <InputText v-model="title" v-bind="titleAttrs" :disabled="props.isLoading" />
        <div class="flex items-center">
          <p class="apx-small-label">
            Email
          </p>
        </div>
        <IconField icon-position="left">
          <InputIcon class="pi pi-envelope" />
          <InputText
            v-model="email"
            class="w-full"
            v-bind="emailAttrs"
            :disabled="props.isLoading"
            :invalid="Boolean(errors['address_emails.0.email'])"
          />
        </IconField>
        <div class="flex items-center">
          <p class="apx-small-label">
            Phone
          </p>
        </div>
        <IconField icon-position="left">
          <InputIcon class="pi pi-phone" />
          <InputText
            v-model="phone"
            class="w-full"
            v-bind="phoneAttrs"
            :disabled="props.isLoading"
          />
        </IconField>
        <slot name="additionalFields" />
        <div class="flex items-start">
          <p class="apx-small-label">
            Address
          </p>
        </div>
        <div class="grid w-full grid-cols-3 grid-rows-auto gap-3">
          <div class="col-span-3">
            <InputText
              v-model="address1"
              v-bind="address1Attrs"
              placeholder="Address line 1"
              class="w-full"
              :disabled="props.isLoading"
            />
          </div>
          <div class="col-span-3">
            <InputText
              v-model="address2"
              v-bind="address2Attrs"
              placeholder="Address line 2"
              class="w-full"
              :disabled="props.isLoading"
            />
          </div>
          <div class="col-span-3 lg:col-span-1">
            <InputText
              v-model="city"
              v-bind="cityAttrs"
              placeholder="City"
              class="max-w-full"
              :disabled="props.isLoading"
            />
          </div>
          <div class="col-span-2 lg:col-span-1">
            <InputText
              v-model="state"
              v-bind="stateAttrs"
              placeholder="State"
              class="max-w-full"
              :disabled="props.isLoading"
            />
          </div>
          <div class="col-span-1">
            <InputText
              v-model="zip"
              v-bind="zipAttrs"
              placeholder="Zip"
              class="max-w-full"
              :disabled="props.isLoading"
            />
          </div>
        </div>
        <div class="col-start-2">
          <Button
            type="submit"
            :label="submitLabel"
            class="w-full"
            :disabled="disableSubmit"
          />
        </div>
      </div>
    </form>
  </div>
</template>
